import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import QuestionPreviewModal from './QuestionPreviewModal';

// Styled components following SongQ design system
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: '#1A1A1A',
  '& .MuiTableHead-root': {
    backgroundColor: '#121212',
  },
  '& .MuiTableCell-root': {
    borderColor: '#333',
    color: '#fff',
  },
  '& .MuiTableCell-head': {
    backgroundColor: '#121212',
    fontWeight: 600,
    color: '#4CAF50',
  },
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  ...(variant === 'outlined' && {
    borderColor: '#333',
    color: '#ccc',
    '&:hover': {
      borderColor: '#4CAF50',
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
    },
  }),
}));

const SongQuestionsDetailView = ({
  song,
  questions,
  isLoading,
  onBackClick,
  onActionMenuClick,
  searchTerm
}) => {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  // Filter questions for this specific song
  const songQuestions = questions.filter(question => 
    question.songs_master_id === song.songs_master_id &&
    (searchTerm === '' || 
     question.question_text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     question.level_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     question.answer_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     question.question_clue?.toLowerCase().includes(searchTerm.toLowerCase()) 
    )
  );

  const getDifficultyColor = (level) => {
    const colors = {
      'Easy': '#4CAF50',
      'Medium': '#FF9800',
      'Hard': '#F44336',
      'Very Hard': '#9C27B0',
      'Expert': '#E91E63'
    };
    return colors[level] || '#2196F3';
  };

  return (
    <Box>
      {/* Header with back button and song info */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <ActionButton
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBackClick}
            sx={{ mr: 2 }}
          >
            Back to Songs
          </ActionButton>

          <Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600 }}>
              {song.song_name}
            </Typography>
            {song.artist && (
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                by {song.artist} • {songQuestions.length} questions
              </Typography>
            )}
          </Box>
        </Box>

        {/* Preview Button */}
        {songQuestions.length > 0 && (
          <ActionButton
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => setPreviewModalOpen(true)}
            sx={{
              borderColor: '#4CAF50',
              color: '#4CAF50',
              '&:hover': {
                borderColor: '#4CAF50',
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
              },
            }}
          >
            Preview Questions
          </ActionButton>
        )}
      </Box>

      {/* Questions Table */}
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Question</TableCell>
              <TableCell>Difficulty</TableCell>
              <TableCell>Answer Type</TableCell>
              {/* <TableCell>Time Limit</TableCell> */}
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress sx={{ color: '#4CAF50' }} />
                </TableCell>
              </TableRow>
            ) : songQuestions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" sx={{ color: '#ccc' }}>
                    {searchTerm ? 'No questions match your search for this song' : 'No questions found for this song'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              songQuestions.map((question) => (
                <TableRow key={question.question_id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#fff', maxWidth: 400 }}>
                      {question.question_text?.length > 120 
                        ? `${question.question_text.substring(0, 120)}...`
                        : question.question_text
                      }
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      label={question.level_name}
                      size="small"
                      sx={{
                        backgroundColor: getDifficultyColor(question.level_name),
                        color: '#fff',
                        fontWeight: 600
                      }}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      label={question.answer_type_display || question.answer_type || 'Multiple Choice'}
                      size="small"
                      sx={{
                        backgroundColor: '#2196F3',
                        color: '#fff',
                        fontWeight: 500
                      }}
                    />
                  </TableCell>
                  
                  {/* <TableCell>
                    <Typography variant="body2" sx={{ color: '#ccc' }}>
                      {formatTimeLimit(question.time_limit_seconds)}
                    </Typography>
                  </TableCell> */}
                  
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#ccc' }}>
                      {new Date(question.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  
                  <TableCell align="center">
                    <IconButton
                      onClick={(e) => onActionMenuClick(e, question)}
                      sx={{ color: '#ccc' }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* Question Preview Modal */}
      <QuestionPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        song={song}
        questionIds={songQuestions.map(q => q.question_id)}
      />
    </Box>
  );
};

export default SongQuestionsDetailView;
